import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { DatePickerModal } from 'react-rainbow-components';
import { Value } from 'react-rainbow-components/components/DatePicker';
import ReactCalendar from 'react-rainbow-components/components/Calendar/index.js';
import CalendarIcon from '@/assets/icons/calendar.svg?react';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { setFrom, setTo } from '@/redux/bookingSlice';
import useClickOutside from '@/hooks/useClickOutside';
import arrowDown from '@/assets/icons/arrow-down.svg';
import PlanSVG from '../planSVG/PlanSVG';
import useResize from '@/hooks/useResize';
import styles from './selectors.module.scss';

function formatDateToSelector(date: string, locale: string = 'default') {
    if (date === '') return '';
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(new Date(date));
}

function formatDateToGlobalState(date: Date) {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

function datesToString(dates: any, locale: string = 'default') {
    if (dates) {
        const startDay = formatDateToSelector(dates[0], locale);

        if (dates.length > 1) {
            const endDay = formatDateToSelector(dates[1], locale);
            return `${startDay} - ${endDay}`;
        }
        return startDay;
    }
    return '';
}

const DateSelector = () => {
    const { from, to } = useAppSelector((state) => state.booking);
    const [calendarValues, setCalendarValues] = useState<Value>(getInitialValues());
    const [isActive, setIsActive] = useState(false);
    const [maxDate, setMaxDate] = useState<Date | undefined>(undefined);
    const oneDayFromNow = DateTime.local().plus({ days: 1 });
    const { width } = useResize();
    const isDesktop = width > 1150;
    const calendarRef = useRef<any>(null);
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();

    useClickOutside(calendarRef, () => {
        if (isActive) {
            setCalendarValues([new Date(from), new Date(to)]);
            setIsActive(false);
        }
    });

    useEffect(() => {
        if (from === '' && to === '') {
            setDefaultValues();
        }
    }, []);

    function getInitialValues() {
        if (from !== '' && to !== '') {
            return [new Date(from), new Date(to)];
        }
        return [];
    }

    const setDefaultValues = () => {
        const defaultStartDate = formatDateToGlobalState(oneDayFromNow.toJSDate());
        const defaultEndDate = formatDateToGlobalState(oneDayFromNow.plus({ days: 3 }).toJSDate());
        dispatch(setFrom(defaultStartDate));
        dispatch(setTo(defaultEndDate));
        setCalendarValues([new Date(defaultStartDate), new Date(defaultEndDate)]);
    };

    const onSelectorClick = () => {
        setIsActive(!isActive);
    };

    const onDateChange = (newDates: Value) => {
        setCalendarValues(newDates);

        if (Array.isArray(newDates)) {
            const maxAvailableDate = DateTime.fromJSDate(newDates[0]).plus({ days: 27 });
            setMaxDate(maxAvailableDate.toJSDate() ?? undefined);
        }

        if (Array.isArray(newDates) && newDates.length > 1) {
            const newFromDate = formatDateToGlobalState(newDates[0]);
            const newToDate = formatDateToGlobalState(newDates[1]);
            dispatch(setFrom(newFromDate));
            dispatch(setTo(newToDate));
            setMaxDate(undefined);

            setTimeout(() => {
                setIsActive(false);
            }, 300);
        }
    };

    const onClose = () => {
        if (from !== '' && to !== '') {
            setCalendarValues([new Date(from), new Date(to)]);
        }
        setIsActive(false);
    };

    const datesString = datesToString([from, to], i18n.language);
    const showCalendarModal = isActive && !isDesktop;
    const showCalendarBox = isActive && isDesktop;

    return (
        <div className={`${styles.selectorContainer} ${styles.daysSelector}`}>
            {/* <p className={styles.selectorName}>{t('days')}</p> */}
            <button className={styles.selector} onClick={onSelectorClick}>
                <PlanSVG svg={<CalendarIcon />} className={styles.icon} />
                <p
                    className={styles.text} // data-travel-date="formatted"
                >
                    {datesString}
                </p>
                <div className={styles.arrow}>
                    <img src={arrowDown} alt="" className={isActive ? styles.arrowActive : ''} />
                </div>
            </button>
            {showCalendarBox && (
                <div ref={calendarRef} className={styles.calendarContainer}>
                    <ReactCalendar
                        variant="double"
                        selectionType="range"
                        locale={i18n.language}
                        value={calendarValues}
                        minDate={oneDayFromNow.toJSDate()}
                        maxDate={maxDate}
                        onChange={onDateChange}
                    />
                </div>
            )}
            {showCalendarModal && (
                <DatePickerModal
                    style={{ backgroundColor: 'white' }}
                    className={styles.calendarModalContainer}
                    isOpen={isActive}
                    value={calendarValues}
                    variant="double"
                    selectionType="range"
                    onChange={onDateChange}
                    onRequestClose={onClose}
                    minDate={oneDayFromNow.toJSDate()}
                    maxDate={maxDate}
                />
            )}
        </div>
    );
};

export default DateSelector;
